// EN/FR copy for Individual Goals (data-cvi-i18n="managed").

const plural = (n, one, many) => (n === 1 ? one : many);

const COPY = {
    en: {
        title: "Individual Goals",
        titleFor: name => `Individual Goals — ${name}`,
        intro: "Agree on measurable outcomes with your manager and keep progress up to date.",
        introPeriod: period => `Goals for ${period}. Agree on measurable outcomes with your manager and keep progress up to date.`,
        loading: "Loading goals…",
        loadError: "Goals could not be loaded.",
        retry: "Try again",
        noPeriod: "There is no active period. Goals open when a period is activated.",
        locked: "This period is closed. Goals are read-only.",
        tabs: { label: "Individual Goals", mine: "My goals", team: "Team members’ goals" },
        actions: {
            propose: "Propose a goal", add: "Add a goal", reuse: "Reuse a previous goal", backToTeam: "Back to team members",
            update: "Update progress", approve: "Approve", reject: "Decline", review: "Final review",
        },
        status: { PROPOSED: "Awaiting approval", ACTIVE: "In progress", COMPLETED: "Evaluated", REJECTED: "Declined", CANCELLED: "Cancelled" },
        groups: {
            PROPOSED: "Awaiting approval", ACTIVE: "In progress", COMPLETED: "Evaluated", CLOSED: "Declined or cancelled",
        },
        groupCount: n => `${n} ${plural(n, "goal", "goals")}`,
        summary: {
            label: "Goals summary",
            goals: "Goals", goalsHelp: n => `${n} approved`,
            awaiting: "Awaiting approval", awaitingHelpManager: "Need your decision", awaitingHelpMine: "With your manager",
            evaluated: "Evaluated", evaluatedHelp: (done, total) => `${done} of ${total} approved goals`,
            progress: "Reported progress", progressHelp: "Weighted by importance · reported by the employee",
        },
        facts: {
            importance: "Importance", share: "Relative weight", shareHelp: "Share of this period’s approved goals",
            due: "Due", overdue: "Overdue", target: "Target", actual: "Actual", indicator: "Linked indicator",
            proposedBy: "Proposed by", assignedBy: "Assigned by", approvedBy: "Approved by", declinedBy: "Declined by",
        },
        progress: "Reported progress",
        notes: { employee: "Employee comment", evidence: "Supporting evidence", manager: "Manager comment" },
        challenge: {
            title: "Challenge or support need", type: "Type of challenge", status: "Status", description: "Challenge description",
            support: "Support needed", action: "Suggested action", optional: "Optional",
            types: {
                "": "No challenge", EXTERNAL_DEPENDENCY: "External or cross-unit dependency", RESOURCE_LIMITATION: "Resource constraint",
                PROCESS_ISSUE: "Process issue", SYSTEM_TOOL: "System or tool", MANAGEMENT_DECISION: "Management decision", OTHER: "Other",
            },
            statuses: { none: "No challenge", open: "Unresolved", in_progress: "In progress", resolved: "Resolved" },
        },
        finalScore: "Final score", outOf: "/ 100",
        stageHint: {
            PROPOSED_MINE: "Your manager reviews this proposal before it becomes active.",
            PROPOSED_MANAGER: "Approve the goal to make it active, or decline it with a comment.",
            ACTIVE_MINE: "Keep progress and evidence up to date. Your manager records the final review.",
            ACTIVE_MANAGER: "Record the final review once the outcome is known.",
            COMPLETED: "Final review recorded. This goal is closed.",
        },
        empty: {
            mineTitle: "You have no goals for this period yet.",
            mineText: "Propose a goal to agree on a measurable outcome with your manager. It becomes active once approved.",
            subjectTitle: "No goals recorded for this person yet.",
            subjectText: "Add a goal to set a measurable outcome for this period.",
            noReports: "No one reports directly to you yet.",
        },
        people: { goals: n => `${n} ${plural(n, "goal", "goals")}`, awaiting: n => `${n} awaiting approval`, open: name => `View goals for ${name}` },
        form: {
            titleTeam: "Add an individual goal", titleMine: "Propose an individual goal",
            descTeam: "The goal becomes active as soon as it is saved.", descMine: "The goal becomes active after your manager approves it.",
            person: "Person", goalTitle: "Goal title", goalTitlePlaceholder: "Example: Reduce customer response time",
            description: "Description and success measure", target: "Target", unit: "Unit", unitPlaceholder: "Percent, hours, count…",
            importance: "Importance", importancePlaceholder: "Select an importance level", due: "Due date",
            indicator: "Linked strategic indicator (optional)", noIndicator: "No direct link",
            required: "Goal title, due date and importance level are required.",
            cancel: "Cancel", saving: "Saving…", saveTeam: "Save and activate", saveMine: "Submit proposal",
            scaleError: "The importance scale could not be loaded.",
        },
        progressForm: {
            title: "Update progress", description: goal => `Report where “${goal}” stands. Your manager sees these updates.`,
            progress: "Progress (%)", actual: "Actual result", evidence: "Evidence, link or context", note: "Comment",
            invalid: "Progress must be between 0% and 100%.", cancel: "Cancel", save: "Save progress", saving: "Saving…",
        },
        decision: {
            approveTitle: "Approve this goal?", rejectTitle: "Decline this goal?",
            approveText: "The goal becomes active and counts toward Individual Goals once evaluated.",
            rejectText: "The goal is closed and does not count toward the CVI. The employee sees your comment.",
            note: "Comment for the employee", noteOptional: "Optional", cancel: "Cancel",
            approve: "Approve goal", reject: "Decline goal", saving: "Saving…",
        },
        review: {
            title: "Final review", description: goal => `Record the final score for “${goal}”.`,
            context: "Employee’s latest update", score: "Final score (0–100)", note: "Manager comment",
            invalid: "The score must be between 0 and 100.",
            warning: "Once recorded, the goal is closed as evaluated and the score can no longer be changed here.",
            cancel: "Cancel", save: "Record final review", saving: "Saving…",
        },
        errors: { server: "Something went wrong on our side. Please try again.", request: "The request could not be completed. Please try again." },
    },
    fr: {
        title: "Objectifs individuels",
        titleFor: name => `Objectifs individuels — ${name}`,
        intro: "Convenez de résultats mesurables avec votre gestionnaire et tenez l’avancement à jour.",
        introPeriod: period => `Objectifs de ${period}. Convenez de résultats mesurables avec votre gestionnaire et tenez l’avancement à jour.`,
        loading: "Chargement des objectifs…",
        loadError: "Les objectifs n’ont pas pu être chargés.",
        retry: "Réessayer",
        noPeriod: "Aucune période n’est active. Les objectifs s’ouvrent à l’activation d’une période.",
        locked: "Cette période est fermée. Les objectifs sont en lecture seule.",
        tabs: { label: "Objectifs individuels", mine: "Mes objectifs", team: "Objectifs des membres de l’équipe" },
        actions: {
            propose: "Proposer un objectif", add: "Ajouter un objectif", reuse: "Réutiliser un objectif antérieur", backToTeam: "Retour aux membres de l’équipe",
            update: "Mettre à jour l’avancement", approve: "Approuver", reject: "Refuser", review: "Revue finale",
        },
        status: { PROPOSED: "En attente d’approbation", ACTIVE: "En cours", COMPLETED: "Évalué", REJECTED: "Refusé", CANCELLED: "Annulé" },
        groups: {
            PROPOSED: "En attente d’approbation", ACTIVE: "En cours", COMPLETED: "Évalués", CLOSED: "Refusés ou annulés",
        },
        groupCount: n => `${n} ${plural(n, "objectif", "objectifs")}`,
        summary: {
            label: "Sommaire des objectifs",
            goals: "Objectifs", goalsHelp: n => `${n} ${plural(n, "approuvé", "approuvés")}`,
            awaiting: "En attente d’approbation", awaitingHelpManager: "Requièrent votre décision", awaitingHelpMine: "Chez votre gestionnaire",
            evaluated: "Évalués", evaluatedHelp: (done, total) => `${done} sur ${total} ${plural(total, "objectif approuvé", "objectifs approuvés")}`,
            progress: "Avancement déclaré", progressHelp: "Pondéré selon l’importance · déclaré par la personne",
        },
        facts: {
            importance: "Importance", share: "Poids relatif", shareHelp: "Part des objectifs approuvés de la période",
            due: "Échéance", overdue: "En retard", target: "Cible", actual: "Réel", indicator: "Indicateur lié",
            proposedBy: "Proposé par", assignedBy: "Attribué par", approvedBy: "Approuvé par", declinedBy: "Refusé par",
        },
        progress: "Avancement déclaré",
        notes: { employee: "Commentaire de la personne", evidence: "Pièce justificative", manager: "Commentaire du gestionnaire" },
        challenge: {
            title: "Défi ou besoin de soutien", type: "Type de défi", status: "Statut", description: "Description du défi",
            support: "Soutien requis", action: "Action suggérée", optional: "Facultatif",
            types: {
                "": "Aucun défi", EXTERNAL_DEPENDENCY: "Dépendance externe ou entre unités", RESOURCE_LIMITATION: "Contrainte de ressources",
                PROCESS_ISSUE: "Problème de processus", SYSTEM_TOOL: "Système ou outil", MANAGEMENT_DECISION: "Décision de la direction", OTHER: "Autre",
            },
            statuses: { none: "Aucun défi", open: "Non résolu", in_progress: "En cours", resolved: "Résolu" },
        },
        finalScore: "Résultat final", outOf: "/ 100",
        stageHint: {
            PROPOSED_MINE: "Votre gestionnaire examine cette proposition avant qu’elle devienne active.",
            PROPOSED_MANAGER: "Approuvez l’objectif pour le rendre actif, ou refusez-le avec un commentaire.",
            ACTIVE_MINE: "Tenez l’avancement et les pièces justificatives à jour. Votre gestionnaire consigne la revue finale.",
            ACTIVE_MANAGER: "Consignez la revue finale lorsque le résultat est connu.",
            COMPLETED: "Revue finale consignée. Cet objectif est fermé.",
        },
        empty: {
            mineTitle: "Vous n’avez encore aucun objectif pour cette période.",
            mineText: "Proposez un objectif pour convenir d’un résultat mesurable avec votre gestionnaire. Il devient actif une fois approuvé.",
            subjectTitle: "Aucun objectif n’est encore consigné pour cette personne.",
            subjectText: "Ajoutez un objectif pour fixer un résultat mesurable pour cette période.",
            noReports: "Personne ne relève encore directement de vous.",
        },
        people: { goals: n => `${n} ${plural(n, "objectif", "objectifs")}`, awaiting: n => `${n} en attente d’approbation`, open: name => `Voir les objectifs de ${name}` },
        form: {
            titleTeam: "Ajouter un objectif individuel", titleMine: "Proposer un objectif individuel",
            descTeam: "L’objectif devient actif dès son enregistrement.", descMine: "L’objectif devient actif après l’approbation de votre gestionnaire.",
            person: "Personne", goalTitle: "Titre de l’objectif", goalTitlePlaceholder: "Exemple\u00a0: Réduire le délai de réponse aux clients",
            description: "Description et mesure du succès", target: "Cible", unit: "Unité", unitPlaceholder: "Pourcentage, heures, nombre…",
            importance: "Importance", importancePlaceholder: "Choisir un niveau d’importance", due: "Échéance",
            indicator: "Indicateur stratégique lié (facultatif)", noIndicator: "Aucun lien direct",
            required: "Le titre, l’échéance et le niveau d’importance de l’objectif sont obligatoires.",
            cancel: "Annuler", saving: "Enregistrement…", saveTeam: "Enregistrer et activer", saveMine: "Soumettre la proposition",
            scaleError: "L’échelle d’importance n’a pas pu être chargée.",
        },
        progressForm: {
            title: "Mettre à jour l’avancement", description: goal => `Indiquez où en est «\u00a0${goal}\u00a0». Votre gestionnaire voit ces mises à jour.`,
            progress: "Avancement (%)", actual: "Résultat réel", evidence: "Pièce justificative, lien ou contexte", note: "Commentaire",
            invalid: "L’avancement doit être compris entre 0\u00a0% et 100\u00a0%.", cancel: "Annuler", save: "Enregistrer l’avancement", saving: "Enregistrement…",
        },
        decision: {
            approveTitle: "Approuver cet objectif?", rejectTitle: "Refuser cet objectif?",
            approveText: "L’objectif devient actif et compte dans les objectifs individuels une fois évalué.",
            rejectText: "L’objectif est fermé et ne compte pas dans le CVI. La personne voit votre commentaire.",
            note: "Commentaire pour la personne", noteOptional: "Facultatif", cancel: "Annuler",
            approve: "Approuver l’objectif", reject: "Refuser l’objectif", saving: "Enregistrement…",
        },
        review: {
            title: "Revue finale", description: goal => `Consignez le résultat final de «\u00a0${goal}\u00a0».`,
            context: "Dernière mise à jour de la personne", score: "Résultat final (0 à 100)", note: "Commentaire du gestionnaire",
            invalid: "Le résultat doit être compris entre 0 et 100.",
            warning: "Une fois consigné, l’objectif est fermé comme évalué et le résultat ne peut plus être modifié ici.",
            cancel: "Annuler", save: "Consigner la revue finale", saving: "Enregistrement…",
        },
        errors: { server: "Un problème est survenu de notre côté. Veuillez réessayer.", request: "La demande n’a pas pu être traitée. Veuillez réessayer." },
    },
};

export function goalCopy(language) {
    return COPY[language === "fr" ? "fr" : "en"];
}
